
import { validationMixin } from 'vuelidate';
import { required, minLength } from 'vuelidate/lib/validators';
import ValidateService from '../../../services/validations/ValidateService';
import { Component, Prop } from 'vue-property-decorator';
import VueApp from '@/@types/app/VueApp';
import notificationService from '@/services/NotificationService';
import { Logger } from '@/other/Logger';
import { Company } from '@/models/Company';
import * as routerNames from '@/routerNames';
import {
  GUIDES_APP,
  buildSessionHopSuffix,
  clearAppContext,
  hasLiveSubscription,
  guidesRedirectAction,
  guidesReturnTo,
  isGuidesContext,
  isGuidesOnlyCompany,
  pickGuidesCompany,
  setAppContext,
} from '@/helpers/appContext';
import { getProductsUrl } from '@/helpers/productsUrl';

const log = new Logger('TwoFactorVerificationForm');

const validations = {
  form: {
    code: {
      required,
      minLength: minLength(6),
      numeric: (value: string) => /^\d+$/.test(value),
    },
  },
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
@Component({
  mixins: [validationMixin],
  validations,
})
export default class TwoFactorVerificationForm extends VueApp {
  @Prop({ type: String, required: true }) userId: string;
  @Prop({ type: String, required: true }) message: string;
  /**
   * Per-attempt token that binds the 2FA code to one login attempt: from the POST /login
   * response in the password flow (SNAP-2357), or from the Google callback URL
   * `#/login/social-2fa/:loginToken` (SNAP-2366). `userId` is only the fallback without it.
   */
  @Prop({ type: String, default: '' }) loginToken: string;

  form = {
    code: '',
  };

  isVerifying = false;
  isResending = false;
  errorMessage = '';
  resendCooldown = 0;
  resendTimer: any | null = null;

  get ValidateService() {
    return new ValidateService(this, this.handleVerification);
  }

  get twoFactorMessage() {
    return this.message || 'Check your email for the verification code';
  }

  get isCodeValid() {
    return this.form.code.length === 6 && /^\d+$/.test(this.form.code);
  }

  onCodeInput(value: string) {
    // Only allow numeric input
    this.form.code = value.replace(/\D/g, '');

    // Auto-submit when 6 digits are entered
    if (this.form.code.length === 6) {
      setTimeout(() => {
        this.handleVerification();
      }, 100);
    }
  }

  onKeyDown(event: KeyboardEvent) {
    // Allow backspace, delete, tab, escape, enter
    if (
      [8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (event.keyCode === 65 && event.ctrlKey === true) ||
      (event.keyCode === 67 && event.ctrlKey === true) ||
      (event.keyCode === 86 && event.ctrlKey === true) ||
      (event.keyCode === 88 && event.ctrlKey === true)
    ) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if ((event.shiftKey || event.keyCode < 48 || event.keyCode > 57) && (event.keyCode < 96 || event.keyCode > 105)) {
      event.preventDefault();
    }
  }

  async handleVerification() {
    // Enter and the sixth-digit auto-submit both reach here; a second request would replay a spent code.
    if (this.isVerifying) return;
    if (!this.isCodeValid) {
      this.ValidateService.validateForm();
      return;
    }

    this.isVerifying = true;
    this.errorMessage = '';

    try {
      await this.$api.verifyTwoFactorCode(this.userId, this.form.code, this.loginToken);

      // Success - the session cookie is set; load the user from it
      await this.completeLogin();
    } catch (error: any) {
      this.isVerifying = false;
      this.handleVerificationError(error);
    }
  }

  async handleResend() {
    if (this.resendCooldown > 0) return;

    this.isResending = true;
    this.errorMessage = '';

    try {
      await this.$api.resendTwoFactorCode(this.userId, this.loginToken);
      notificationService.success('New verification code sent to your email');
      this.startResendCooldown();
    } catch (error: any) {
      this.handleResendError(error);
    } finally {
      this.isResending = false;
    }
  }

  async loadSessionUser() {
    const res = await this.$api.getUser();
    this.$auth.setUser(res.data.user);
    this.$auth.setCompany(res.data.user.company);
    // The company hop (and the Guides company pick) needs the user's companies, as in LoginForm.
    if (res.data?.companies) {
      this.$auth.setUserCompanies(res.data.companies.map((item) => Company.parse(item)));
    }
  }

  async completeLogin() {
    try {
      // SNAP-2309: the API set the session cookie on the 2FA response. Load the user from it.
      // Use the same login completion logic as the original LoginForm
      await this.loadSessionUser();

      // Complete the login flow
      await this.finalizeLogin();
    } catch (error) {
      log.error('Error completing login:', error);
      notificationService.error('Login completed but failed to load user data. Please refresh the page.');
    }
  }

  async finalizeLogin() {
    // Import necessary modules
    const { dispatchNotificationsSidebarReset } = await import('@/store/dispatchers/notificationsSidebarDispatchers');
    const extensionService = await import('@/services/ExtensionService');

    try {
      // Check company activation and redirect logic
      if (await this.checkAndRedirectToFirstCompany()) return;

      // Login for extension
      extensionService.default.logInUser();

      // Reset notifications
      await dispatchNotificationsSidebarReset();

      // H1: same Guides redirect LoginForm applies after a successful login.
      if (this.redirectToGuidesIfNeeded()) return;

      // Handle redirect
      const redirect = this.$route.query.redirect;
      if (typeof redirect === 'string' && redirect.startsWith('/')) {
        await this.$router.push(redirect);
      } else {
        await this.$router.push({ name: routerNames.HOME });
      }
    } catch (error) {
      log.error('Error finalizing login:', error);
      notificationService.error('Login completed but failed to redirect. Please refresh the page.');
    }
  }

  /**
   * H1: with a Guides context, a company that is BOTH paid AND howto-entitled goes to the Guides
   * app; one paid for something else (e.g. Snaplore/kb) is upsold to the Guides plans page; an
   * unpaid one falls through to the billing flow. Mirrors LoginForm.redirectToGuidesIfNeeded.
   */
  redirectToGuidesIfNeeded(): boolean {
    const company = this.$store.state.company;
    if (!isGuidesContext(this.$route?.query) && !isGuidesOnlyCompany(company)) return false;
    const action = guidesRedirectAction(company);

    if (action === 'products') {
      const returnTo = guidesReturnTo(this.$route?.query);
      clearAppContext();
      this.hardNavigate(getProductsUrl(returnTo));
      return true;
    }

    if (action === 'plans-upsell') {
      clearAppContext();
      this.$router.push({ name: routerNames.BILLING_PLANS, query: { app: GUIDES_APP } });
      return true;
    }

    setAppContext(GUIDES_APP);
    return false;
  }

  /** Hard navigation (another origin or the products app); a seam so specs can observe the URL. */
  hardNavigate(url: string) {
    document.location.href = url;
  }

  async checkAndRedirectToFirstCompany() {
    const isDefaultDomain = document.location.origin === process.env.VUE_APP_URL;
    const userCompanies = this.$store.state.userCompanies;

    const isGuides = isGuidesContext(this.$route?.query);
    const guidesCompany = isGuides ? pickGuidesCompany(userCompanies, this.$store.state.user?.id) : null;
    const firstCompany =
      guidesCompany ?? userCompanies?.find((company) => hasLiveSubscription(company)) ?? userCompanies?.[0] ?? null;

    if (isDefaultDomain && firstCompany?.domain && !document.location.origin.includes(firstCompany?.domain)) {
      const res = await this.$api.company.switch(firstCompany?.id).catch(log.error);
      if (res) {
        const localStorageService = await import('@/services/LocalStorageService');
        localStorageService.default.clear();
        // SNAP-2309: the switch set the session cookie. session=1 makes the target finish the login. Redirect and
        // (in a Guides journey) app + returnTo travel with it, as the origin changes.
        const querySuffix = buildSessionHopSuffix(this.$route?.query, isGuides);
        this.hardNavigate(`${firstCompany.getDomainAddress()}/#/login${querySuffix}`);
        return true;
      }
    }

    // Same origin, as in LoginForm: a Guides login must still land in the Guides company, not in whichever
    // company the session happened to start in. Switch in place and carry on with it.
    if (guidesCompany && guidesCompany.id !== this.$store.state.company?.id) {
      const res = await this.$api.company.switch(guidesCompany.id).catch(log.error);
      // SNAP-2309: the switch set the session cookie for that company, so load it.
      if (res) await this.loadSessionUser();
    }
    return false;
  }

  handleVerificationError(error: any) {
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data as any;

      switch (status) {
        case 400:
          if (data.error && data.error.includes('expired')) {
            this.errorMessage = 'Code has expired. Please request a new one.';
          } else if (data.error && data.error.toLowerCase().includes('invalid')) {
            this.errorMessage = 'Invalid verification code. Please try again.';
          } else {
            this.errorMessage = data.error || 'Invalid verification code. Please try again.';
          }
          break;
        case 401:
          if (data.code === 'two_factor_required') {
            this.errorMessage = 'Two-factor authentication is required. Please enter your verification code.';
          } else {
            this.errorMessage = 'Authentication failed. Please try again.';
          }
          break;
        case 409:
          // The code is already spent when the Google link loses its race, so the user must restart sign-in.
          this.errorMessage = data.error || 'This sign-in could not be completed. Please try signing in again.';
          break;
        case 429:
          this.errorMessage = 'Too many failed attempts. Please request a new code.';
          break;
        case 500:
          this.errorMessage = 'Unable to verify code. Please check your connection and try again.';
          break;
        default:
          this.errorMessage = 'Unable to verify code. Please check your connection and try again.';
      }
    } else {
      this.errorMessage = 'Unable to verify code. Please check your connection and try again.';
    }

    // Clear the code input for retry
    this.form.code = '';
    this.$nextTick(() => {
      (this.$refs.codeInput as any)?.focus();
    });
  }

  handleResendError(error: any) {
    if (error.response) {
      const data = error.response.data as any;
      this.errorMessage = data.error || 'Unable to send new code. Please try again.';
    } else {
      this.errorMessage = 'Unable to send new code. Please check your connection and try again.';
    }
  }

  startResendCooldown() {
    this.resendCooldown = 60; // 60 seconds cooldown
    this.resendTimer = setInterval(() => {
      this.resendCooldown--;
      if (this.resendCooldown <= 0) {
        this.clearResendTimer();
      }
    }, 1000);
  }

  clearResendTimer() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
    this.resendCooldown = 0;
  }

  goBackToLogin() {
    this.$emit('back-to-login');
  }

  mounted() {
    // Auto-focus on the code input
    this.$nextTick(() => {
      (this.$refs.codeInput as any)?.focus();
    });

    // Automatically send the 2FA code when component mounts
    this.sendInitialCode();
  }

  async sendInitialCode() {
    try {
      await this.$api.sendTwoFactorCode(this.userId, this.loginToken);
      // Don't show success message for initial send, just start cooldown
      this.startResendCooldown();
    } catch (error) {
      // If sending fails, show error but don't block the user
      log.error(`Failed to send initial 2FA code: ${error}`);
    }
  }

  beforeDestroy() {
    this.clearResendTimer();
  }
}
